import { Text, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceArea, CartesianGrid,
} from 'recharts';
import { fetchTelemetry } from '../../api/devices.api';
import { useT } from '../../i18n';
import { useIntlFormat } from '../../hooks/useIntlFormat';

/**
 * F5 — Temperatura del embarque con la banda de rango permitido.
 *
 * La banda importa tanto como la línea: una gráfica de temperatura sin el rango
 * dibujado obliga a quien la ve a recordar de memoria si 8°C está bien o mal.
 */

const useStyles = makeStyles({
  root:   { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  title:  { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  stat:   { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  empty:  { padding: '14px 16px', border: `1px dashed ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

interface Props {
  shipmentId: string;
  tempMinC?: number;
  tempMaxC?: number;
}

export function ShipmentTemperatureChart({ shipmentId, tempMinC, tempMaxC }: Props) {
  const s = useStyles();
  const t = useT();
  const { fmtDateTime } = useIntlFormat();

  const { data: readings, isLoading, isError } = useQuery({
    queryKey: ['telemetry', 'SHIPMENT', shipmentId],
    queryFn: () => fetchTelemetry('SHIPMENT', shipmentId),
  });

  if (isLoading) return <Spinner size="tiny" />;
  if (isError) return <div className={s.empty}>{t.investorLogistics.telemetryError}</div>;

  const points = (readings ?? []).filter(r => r.temperatureC !== undefined);

  // Sin dispositivo no se dibuja una gráfica vacía: se dice que no hay.
  if (!points.length) return <div className={s.empty}>{t.investorLogistics.noTelemetry}</div>;

  const inRange = tempMinC !== undefined && tempMaxC !== undefined
    ? points.filter(r => r.temperatureC! >= tempMinC && r.temperatureC! <= tempMaxC).length
    : null;
  const inRangePct = inRange !== null ? Math.round((inRange / points.length) * 100) : null;

  const chartData = points.map(r => ({
    time: fmtDateTime(r.recordedAt),
    temp: r.temperatureC,
  }));

  return (
    <div className={s.root}>
      <Text className={s.title}>{t.investorLogistics.temperatureTitle}</Text>

      {inRangePct !== null && (
        <Text className={s.stat}>
          {t.investorLogistics.inRangePct}: {inRangePct}% · {t.investorLogistics.tempRange}: {tempMinC}–{tempMaxC}°C
        </Text>
      )}

      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" tick={{ fontSize: 10 }} minTickGap={40} />
          <YAxis unit="°C" tick={{ fontSize: 10 }} />
          <Tooltip />
          {tempMinC !== undefined && tempMaxC !== undefined && (
            <ReferenceArea
              y1={tempMinC}
              y2={tempMaxC}
              fill={tokens.colorPaletteGreenBackground2}
              fillOpacity={0.3}
            />
          )}
          <Line type="monotone" dataKey="temp" dot={false} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
